import type {
  ConnectionHintSource,
  ConnectionValueSource,
} from '../../services/integration-api.js';

/**
 * What a connection's name and instruction are, in the words the person editing them needs — and above
 * all whether the value in front of them is INHERITED.
 *
 * A name resolves in three steps (canon §3): this workspace's profile, the connection's own value, then
 * a computed stand-in. The three read identically in an input box, and the difference decides what a
 * keystroke does: on an inherited value the first edit CREATES this workspace's profile, and everywhere
 * else the person keeps editing what is already theirs. Left unsaid, somebody renames a connection here
 * and finds the old name waiting in the next workspace.
 *
 * `'default'` earns its own sentence rather than sharing the inherited one. It is the only state where
 * nobody has named the connection at all, and a person who reads "inherited" about a name we made up
 * will go looking for the place it was set.
 *
 * The notes live here, apart from the dialogs, because the connect form and an existing connection's
 * settings both show them: two hand-written copies is how the same state ends up described two ways.
 */
const LABEL_NOTES: Record<Exclude<ConnectionValueSource, null>, string> = {
  profile: 'This name was written for this workspace.',
  base: 'Inherited from the connection itself. Change it here and this workspace gets a name of its own, while your other workspaces keep this one.',
  default:
    'Nobody has named this connection — this is a stand-in built from the provider. Type a name and this workspace gets one of its own.',
};

/** The same for the instruction, which has no computed stand-in and so has no third state. */
const HINT_NOTES: Record<Exclude<ConnectionHintSource, null>, string> = {
  profile: 'This instruction was written for this workspace.',
  base: 'Inherited from the connection itself. Change it here and this workspace gets an instruction of its own, while your other workspaces keep this one.',
};

/**
 * What to say under the name field about where the name came from, or `null` when there is nothing
 * honest to say — a source this build does not know. Saying nothing is deliberate: a guess here is a
 * claim about what an edit will do.
 */
export function labelOriginNote(source: ConnectionValueSource): string | null {
  return source === null ? null : LABEL_NOTES[source];
}

/**
 * The same for the instruction. `null` covers both an unknown source and a connection that describes
 * itself nowhere — there is no value to call inherited in either case.
 */
export function usageHintOriginNote(
  source: ConnectionHintSource,
): string | null {
  return source === null ? null : HINT_NOTES[source];
}
